import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import { toast } from "sonner"
import { z } from "zod"
import { useSignInEchoes, useSignInEmail, useSignInLine } from "@/api/client/auth"
import logo from "@/assets/logo.png"
import { Button } from "@/components/ui/button/button"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form/form"
import { Input } from "@/components/ui/input/input"
import { ECHOES_ENABLED } from "@/lib/config"
import { useSessionRefetch } from "@/store/session"

const signinSchema = z.object({
  email: z
    .string()
    .min(1, "メールアドレスを入力してください")
    .email("有効なメールアドレスを入力してください"),
  password: z
    .string()
    .min(8, "パスワードは8文字以上である必要があります")
    .regex(/^(?=.*[a-zA-Z])(?=.*\d)[A-Za-z0-9]+$/, "パスワードは半角英数字で入力してください"),
})

type SigninFormData = z.infer<typeof signinSchema>

function getValidRedirectPath(redirect: string | null): string | undefined {
  if (redirect?.startsWith("/") && !redirect.startsWith("//")) {
    return redirect
  }
  return undefined
}

export function SigninPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const redirectTo = getValidRedirectPath(searchParams.get("redirect"))
  const form = useForm<SigninFormData>({
    resolver: zodResolver(signinSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  })

  const { mutateAsync: signInEmail, isPending } = useSignInEmail()
  const refetchSession = useSessionRefetch()

  const { mutateAsync: signInLine, isPending: isLinePending } = useSignInLine({
    onSuccess: (data) => {
      if (data.url) {
        window.location.href = data.url
      }
    },
    onError: (error) => {
      console.error("LINE login error:", error)
      toast.error("LINEログインに失敗しました")
    },
  })

  const { mutateAsync: signInEchoes, isPending: isEchoesPending } = useSignInEchoes({
    onSuccess: (data) => {
      if (data.url) {
        window.location.href = data.url
      }
    },
    onError: (error) => {
      console.error("Echoes login error:", error)
      toast.error("Echoesログインに失敗しました")
    },
  })

  const onSubmit = async (data: SigninFormData) => {
    try {
      await signInEmail(data)
      // セッション切れのあとは「未ログイン」のセッションがキャッシュに残っており、
      // そのまま遷移すると MainLayout にログイン画面へ戻されるため取り直す
      await refetchSession()
      toast.success("ログインに成功しました")
      navigate(redirectTo || "/")
    } catch (error) {
      console.error("Sign in error:", error)
      const authError = error as { code?: string; error?: string; message?: string }
      if (
        authError.code === "EMAIL_NOT_VERIFIED" ||
        authError.error === "EMAIL_NOT_VERIFIED" ||
        authError.message === "Email not verified"
      ) {
        toast.error("メールアドレス認証を完了してください")
      }
    }
  }

  const handleLineLogin = () => {
    signInLine({ callbackURL: redirectTo || "/" })
  }

  const handleEchoesLogin = () => {
    signInEchoes({ callbackURL: redirectTo || "/" })
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Main Content */}
      <div className="flex-1 flex flex-col items-center px-4 pb-8">
        {/* Logo and Description */}
        <div className="w-full max-w-sm mb-10 text-center">
          <div className="flex justify-center mb-4">
            <img src={logo} alt="AGENTCALL エージェントコール" className="w-full h-auto" />
          </div>
          <p className="text-sm text-slate-500 leading-relaxed">
            複数の物件や部屋を簡単に管理し
            <br />
            賃借人とのコミュニケーションを効率化します。
          </p>
        </div>

        {/* Title */}
        <h2 className="text-lg font-bold text-slate-700 mb-10">アカウントにログイン</h2>

        {/* Sign In Form */}
        <div className="w-full max-w-sm space-y-10">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
              {/* Email Field */}
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-semibold text-slate-600">
                      メールアドレス
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="name@company.jp"
                        type="email"
                        autoComplete="email"
                        className="h-11"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Password Field */}
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-semibold text-slate-600">
                      パスワード
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="*****"
                        type="password"
                        autoComplete="current-password"
                        className="h-11"
                        {...field}
                      />
                    </FormControl>
                    <p className="text-sm text-slate-400">
                      半角英数字、8文字以上で入力してください
                    </p>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Buttons */}
              <div className="space-y-4 pt-3">
                <Button
                  type="submit"
                  className="w-full h-11 font-bold"
                  disabled={form.formState.isSubmitting}
                  loading={isPending}
                >
                  ログイン
                </Button>

                <Button
                  type="button"
                  variant="outline"
                  className="w-full h-11 font-bold text-primary"
                  disabled={form.formState.isSubmitting || isPending}
                  asChild
                >
                  <Link to="/signup">アカウントの新規登録</Link>
                </Button>
              </div>

              {/* Forgot Password Link */}
              <div className="text-center">
                <Link
                  to="/password-issue"
                  className="text-sm font-bold text-slate-400 hover:text-slate-500"
                >
                  パスワードを忘れた場合はこちらから
                </Link>
              </div>
            </form>
          </Form>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-200" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="bg-slate-50 px-2 text-slate-500 font-medium">
                その他のログイン方法
              </span>
            </div>
          </div>

          {/* LINE Login Button */}
          <button
            type="button"
            onClick={handleLineLogin}
            disabled={isLinePending || isPending}
            className="w-full h-11 bg-[#06C755] hover:bg-[#05B04A] disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold rounded-md flex items-center justify-center gap-3 transition-colors"
          >
            <svg
              width="32"
              height="30"
              viewBox="0 0 32 30"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M32 12.7175C32 5.70578 24.8226 0 16 0C7.17742 0 0 5.70578 0 12.7175C0 19.0051 5.69875 24.2716 13.3801 25.2662C13.902 25.3763 14.6113 25.6026 14.7898 26.0389C14.9517 26.4355 14.8962 27.0569 14.8422 27.4565C14.8422 27.4565 14.6548 28.5627 14.6143 28.7978L14.6083 28.8312C14.5317 29.2549 14.3376 30.3287 16 29.6439C17.7081 28.9403 25.214 24.3289 28.5703 20.5432C30.8887 18.0531 32 15.5205 32 12.7175ZM26.6761 16.7677H22.1771C22.0976 16.7677 22.0213 16.7368 21.965 16.6817C21.9088 16.6266 21.8772 16.5518 21.8772 16.4739V9.62814C21.8772 9.55022 21.9088 9.47548 21.965 9.42038C22.0213 9.36528 22.0976 9.33433 22.1771 9.33433H26.6761C26.7557 9.33433 26.832 9.36528 26.8882 9.42038C26.9445 9.47548 26.9761 9.55022 26.9761 9.62814V10.7402C26.9761 10.8181 26.9445 10.8929 26.8882 10.948C26.832 11.0031 26.7557 11.034 26.6761 11.034H23.6213V12.1872H26.6761C26.7557 12.1872 26.832 12.2182 26.8882 12.2733C26.9445 12.3284 26.9761 12.4031 26.9761 12.481V13.6107C26.9761 13.6887 26.9445 13.7634 26.8882 13.8185C26.832 13.8736 26.7557 13.9045 26.6761 13.9045H23.6213V15.0592H26.6761C26.7557 15.0592 26.832 15.0902 26.8882 15.1453C26.9445 15.2004 26.9761 15.2751 26.9761 15.353V16.4636C26.9775 16.5031 26.9708 16.5424 26.9563 16.5792C26.9419 16.6161 26.92 16.6497 26.892 16.6781C26.864 16.7064 26.8305 16.729 26.7934 16.7444C26.7563 16.7598 26.7164 16.7677 26.6761 16.7677ZM10.2614 16.6817C10.2051 16.7368 10.1288 16.7677 10.0493 16.7677L10.0598 16.7633H5.56075C5.4812 16.7633 5.40492 16.7323 5.34867 16.6772C5.29242 16.6222 5.26082 16.5474 5.26082 16.4695V9.63401C5.26082 9.55609 5.29242 9.48136 5.34867 9.42626C5.40492 9.37116 5.4812 9.3402 5.56075 9.3402H6.6945C6.77405 9.3402 6.85035 9.37116 6.90659 9.42626C6.96284 9.48136 6.99444 9.55609 6.99444 9.63401V15.0695H10.0493C10.1288 15.0695 10.2051 15.1005 10.2614 15.1556C10.3176 15.2107 10.3492 15.2854 10.3492 15.3633V16.4739C10.3492 16.5518 10.3176 16.6266 10.2614 16.6817ZM12.7547 9.32992H11.6209C11.452 9.32992 11.315 9.46409 11.315 9.62961V16.4695C11.315 16.635 11.452 16.7692 11.6209 16.7692H12.7547C12.9236 16.7692 13.0606 16.635 13.0606 16.4695V9.62961C13.0606 9.46409 12.9236 9.32992 12.7547 9.32992ZM19.3457 9.32845H20.4795C20.5194 9.32844 20.5589 9.33623 20.5957 9.35137C20.6325 9.3665 20.6658 9.38866 20.6937 9.41657C20.7217 9.44448 20.7436 9.47757 20.7583 9.5139C20.7731 9.55023 20.7802 9.58906 20.7794 9.62814V16.4431C20.7794 16.521 20.7478 16.5957 20.6916 16.6508C20.6353 16.7059 20.559 16.7369 20.4795 16.7369H19.3517C19.3244 16.7364 19.2973 16.733 19.2708 16.7266H19.2003L19.1823 16.7148C19.1517 16.6943 19.1252 16.6684 19.1043 16.6384L15.9055 12.4076V16.5106C15.9055 16.5886 15.8739 16.6633 15.8176 16.7184C15.7614 16.7735 15.6851 16.8044 15.6056 16.8044H14.4703C14.3908 16.8044 14.3145 16.7735 14.2582 16.7184C14.202 16.6633 14.1704 16.5886 14.1704 16.5106V9.67074C14.1704 9.59282 14.202 9.51809 14.2582 9.46299C14.3145 9.40789 14.3908 9.37693 14.4703 9.37693H15.7405L15.757 9.38574H15.766L15.7825 9.3975H15.7915L15.808 9.41071L15.826 9.42835C15.8357 9.43766 15.8442 9.448 15.8515 9.4592L19.0458 13.6856V9.62226C19.0458 9.54433 19.0774 9.46961 19.1337 9.41451C19.1899 9.35941 19.2662 9.32845 19.3457 9.32845Z"
                fill="white"
              />
            </svg>

            <span>{isLinePending ? "LINEログイン中..." : "LINEでログイン"}</span>
          </button>

          {/* Echoes Login Button */}
          {/* TODO: 仮デザイン。Echoesの正式なブランドカラー・ロゴが確定し次第、ボタンの見た目を差し替える */}
          {ECHOES_ENABLED && (
            <button
              type="button"
              onClick={handleEchoesLogin}
              disabled={isEchoesPending || isLinePending || isPending}
              className="w-full h-11 bg-slate-900 hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold rounded-md flex items-center justify-center gap-3 transition-colors"
            >
              <span>{isEchoesPending ? "Echoesログイン中..." : "Echoesでログイン"}</span>
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
